import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Fish, AlertTriangle, Bug, Stethoscope } from 'lucide-react';
import { RecordCard, MetricBox, StatusBadge } from '@/features/shared/components/records';
import type { FishHealthCheck } from '@/api/fishHealthApi';
import { buildRoute } from '@/shared/lib/constants';
import { format } from 'date-fns';

interface FishHealthRecordCardProps {
  check: FishHealthCheck;
  farmId: string;
  pondName?: string;
}

const riskColors = {
  low: 'success' as const,
  medium: 'warning' as const,
  high: 'error' as const,
  critical: 'error' as const,
};

export const FishHealthRecordCard: React.FC<FishHealthRecordCardProps> = ({
  check,
  farmId,
  pondName,
}) => {
  const navigate = useNavigate();

  // Calculate mortality rate if not provided
  const mortalityRate = check.mortalityRate ??
    (check.mortalityCount && check.sampleSize > 0
      ? ((check.mortalityCount / check.sampleSize) * 100)
      : 0);

  const hasParasite = check.parasiteCode && check.parasiteName;
  const hasTreatment = check.treatmentApplied;
  const hasSymptoms = check.physicalSymptoms;

  const handleClick = () => {
    navigate(buildRoute.fishHealthDetail(farmId, check.id, check.pondId));
  };

  return (
    <RecordCard
      icon={Fish}
      title="Fish Health Check"
      subtitle={`${pondName || check.pond?.name || `Pond ${check.pondId.slice(0, 8)}`} • ${format(new Date(check.checkedAt), 'PPpp')}`}
      topRight={
        <>
          {check.healthScore !== undefined && (
            <StatusBadge
              variant={
                check.healthScore >= 80
                  ? 'success'
                  : check.healthScore >= 60
                  ? 'warning'
                  : 'error'
              }
            >
              Score: {check.healthScore.toFixed(0)}
            </StatusBadge>
          )}
          {check.riskLevel && (
            <StatusBadge variant={riskColors[check.riskLevel]}>
              {check.riskLevel} risk
            </StatusBadge>
          )}
          {hasParasite && (
            <StatusBadge variant="warning" showIcon={false}>
              <Bug className="h-3 w-3 mr-1" />
              Parasite
            </StatusBadge>
          )}
          {hasTreatment && (
            <StatusBadge variant="info" showIcon={false}>
              <Stethoscope className="h-3 w-3 mr-1" />
              Treated
            </StatusBadge>
          )}
        </>
      }
      metricsGrid={
        <>
          <MetricBox
            label="Avg Weight"
            value={`${check.averageWeight.toFixed(1)}g`}
            color="blue"
          />
          <MetricBox
            label="Sample Size"
            value={`${check.sampleSize} fish`}
            color="emerald"
          />
          {check.mortalityCount !== undefined && check.mortalityCount > 0 && (
            <MetricBox
              label="Mortality"
              value={`${check.mortalityCount}`}
              color={mortalityRate > 5 ? 'red' : 'amber'}
              subtext={`${mortalityRate.toFixed(1)}%`}
            />
          )}
          {check.feedConversionRatio !== undefined && (
            <MetricBox
              label="FCR"
              value={check.feedConversionRatio.toFixed(2)}
              color={
                check.feedConversionRatio < 1.5
                  ? 'emerald'
                  : check.feedConversionRatio < 2.0
                  ? 'amber'
                  : 'red'
              }
            />
          )}
        </>
      }
      alert={
        hasParasite ? (
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <span>
              Parasite detected: {check.parasiteCode} - {check.parasiteName}
            </span>
          </div>
        ) : hasSymptoms ? (
          <div className="flex items-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            <span className="line-clamp-1">Symptoms: {check.physicalSymptoms}</span>
          </div>
        ) : undefined
      }
      footer={
        check.behaviorObservations ? (
          <p className="line-clamp-2">{check.behaviorObservations}</p>
        ) : check.observations ? (
          <p className="line-clamp-2">{check.observations}</p>
        ) : (
          <p className="italic text-slate-400">No observations</p>
        )
      }
      onClick={handleClick}
    />
  );
};
